import * as React from 'react';
import { classes, useSlug } from '@bloomberg/media-ui-core';

import type { GlobalProps } from '../types';

import styles from './Eyebrow.module.css';

export type EyebrowProps = GlobalProps & {
  sectionTitle: string;
  sectionTitleLink?: string;
  subsectionTitle?: string;
  subsectionTitleLink?: string;
  cobrand?: string;
  isOpinion?: boolean;
  storyType?: string;
  opinionStoryType?: string;
  version?: 'v1' | 'v2';
};

type LinkProps = {
  link?: string;
  children: React.ReactNode;
};

type OpinionSubsectionProps = {
  opinionStoryType?: string;
  authors?: string;
};

export const ConditionalLink: React.FunctionComponent<LinkProps> = ({
  link = null,
  children,
}) => {
  return link ? <a href={link}>{children}</a> : <>{children}</>;
};

const OpinionSubsection = (props: OpinionSubsectionProps) => {
  const { opinionStoryType, authors } = props;

  return (
    <div className={styles.opinionSubsection}>
      {authors && <span className={styles.authors}>{authors}</span>}
      {authors && opinionStoryType && (
        <span className={styles.opinionDivider}>, </span>
      )}
      {opinionStoryType && (
        <span className={styles.opinionStoryType}>{opinionStoryType}</span>
      )}
    </div>
  );
};

export const Eyebrow = React.forwardRef<HTMLDivElement, EyebrowProps>(
  function EyebrowComponent(props: EyebrowProps, ref) {
    const {
      sectionTitle,
      sectionTitleLink,
      subsectionTitle,
      subsectionTitleLink,
      cobrand,
      isOpinion,
      storyType,
      opinionStoryType,
      version = 'v1',
      name = 'eyebrow',
      unstyled,
    } = props;

    const componentName = useSlug(name);

    const isLiveblog = storyType === 'liveblog';
    const isNewsletter = storyType === 'newsletter_campaign';

    const eyebrowClass = classes(
      [
        styles.eyebrow,
        {
          [styles.cobrand]: Boolean(cobrand),
          [styles.opinion]: isOpinion,
          [styles.liveblog]: isLiveblog,
          [styles.newsletter]: isNewsletter,
        },
      ],
      {
        ...props,
        unstyled,
        name,
      }
    );

    const subsectionTitleClass = classes([
      styles.subsectionTitle,
      {
        [styles.cobrand]: Boolean(cobrand),
      },
    ]);

    const EyebrowV1 = () => {
      return (
        <div className={eyebrowClass} ref={ref} data-component={componentName}>
          <div className={styles.sectionTitle} data-component="section-title">
            <ConditionalLink link={sectionTitleLink}>
              {sectionTitle}
            </ConditionalLink>
          </div>
          {subsectionTitle && (
            <div
              className={subsectionTitleClass}
              data-component="subsection-title"
            >
              <ConditionalLink link={subsectionTitleLink}>
                {subsectionTitle}
              </ConditionalLink>
            </div>
          )}
        </div>
      );
    };

    const EyebrowV2 = () => {
      return (
        <div className={eyebrowClass} ref={ref} data-component={componentName}>
          <div className={styles.sectionTitle}>
            <ConditionalLink link={sectionTitleLink}>
              {sectionTitle}
            </ConditionalLink>
          </div>
          {isOpinion && (
            <OpinionSubsection
              authors={subsectionTitle}
              opinionStoryType={opinionStoryType}
            />
          )}
          {!isOpinion && subsectionTitle && (
            <div className={subsectionTitleClass}>
              <ConditionalLink link={subsectionTitleLink}>
                {subsectionTitle}
              </ConditionalLink>
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        {version === 'v1' && <EyebrowV1 />}
        {version === 'v2' && <EyebrowV2 />}
      </>
    );
  }
);
